import React from 'react';
import { Box, Typography, IconButton, Tooltip, Chip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { FunnelSlide } from '../../types';

interface FunnelSlideViewerProps {
    slides: FunnelSlide[];
    onSlideRegenerate: (position: number) => void;
    onSlideView: (slide: FunnelSlide) => void;
}

const FunnelSlideViewer: React.FC<FunnelSlideViewerProps> = ({
                                                                 slides,
                                                                 onSlideRegenerate,
                                                                 onSlideView
                                                             }) => {
    if (slides.length === 0) {
        return (
            <Typography variant="body1" color="text.secondary">
                No slides available
            </Typography>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {slides.map((slide) => {
                const isCompleted = slide.status === 'completed';
                const isFailed = slide.status === 'failed';
                const isPending = slide.status === 'pending';

                return (
                    <Box
                        key={slide.position}
                        sx={{
                            position: 'relative',
                            width: 200,
                            height: 200,
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: `2px solid ${
                                isFailed ? '#f44336' :
                                    isCompleted ? '#4caf50' :
                                        '#e0e0e0'
                            }`,
                            cursor: isCompleted && slide.image_url ? 'pointer' : 'default',
                            bgcolor: isFailed ? '#ffebee' : 'background.paper',
                            '&:hover': {
                                borderColor: isCompleted ? '#1976d2' : undefined,
                                transform: isCompleted ? 'scale(1.02)' : 'none',
                            },
                            transition: 'all 0.2s ease-in-out',
                        }}
                        onClick={() => isCompleted && slide.image_url && onSlideView(slide)}
                    >
                        {/* Изображение слайда или плейсхолдер */}
                        {isCompleted && slide.image_url ? (
                            <img
                                src={slide.image_url}
                                alt={`Slide ${slide.position}: ${slide.slide_type}`}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column',
                                    bgcolor: '#f5f5f5',
                                }}
                            >
                                <Typography variant="body2" color="text.secondary" align="center">
                                    {isFailed ? 'Generation Failed' : isPending ? 'Generating...' : 'Pending'}
                                </Typography>
                                {slide.error_message && (
                                    <Typography variant="caption" color="error" sx={{ mt: 1, px: 1 }}>
                                        {slide.error_message}
                                    </Typography>
                                )}
                            </Box>
                        )}

                        {/* Чип с информацией о слайде */}
                        <Chip
                            label={`${slide.position}. ${slide.slide_type}`}
                            size="small"
                            sx={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                bgcolor: 'rgba(0, 0, 0, 0.7)',
                                color: 'white',
                                fontSize: '0.75rem',
                            }}
                        />

                        {/* Кнопка регенерации */}
                        {(isCompleted || isFailed) && (
                            <Tooltip title="Regenerate Slide">
                                <IconButton
                                    size="small"
                                    sx={{
                                        position: 'absolute',
                                        bottom: 8,
                                        right: 8,
                                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                                        '&:hover': {
                                            bgcolor: 'rgba(255, 255, 255, 1)',
                                        },
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onSlideRegenerate(slide.position);
                                    }}
                                >
                                    <RefreshIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}

                        {/* Индикатор стоимости */}
                        {slide.cost && slide.cost > 0 && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: 8,
                                    left: 8,
                                    bgcolor: 'rgba(0, 0, 0, 0.7)',
                                    color: 'white',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    fontSize: '0.75rem',
                                }}
                            >
                                ${slide.cost.toFixed(3)}
                            </Box>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
};

export default FunnelSlideViewer;
